import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { EditorState, convertToRaw } from "draft-js";
import createEmojiMartPlugin from 'draft-js-emoji-mart-plugin';
import data from 'emoji-mart/data/facebook.json';
import { Container, StyledEditor, SendIcon, SendButton, EditorWrapper, MicIcon, SendContainer } from './styles';
import { useMessageState, useChatState, useTheme, useAppState } from '../../../data';
import { MessageType } from '../../../chat-api';
import Audio from "../../Audio/Audio";

export const emojiPlugin = createEmojiMartPlugin({
  data,
  set: 'facebook'
});

export interface TextInputProps {
  textColor?: string;
  backgroundColor?: string;
  placeholderColor?: string;
  sendIconColor?: string;
}

const TextInput: React.FC<TextInputProps> = () => {
  const { getTranslation } = useAppState();
  const { textInput } = useTheme().footer!;
  const [isReady, setIsReady] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const { sendMessage, isRecordingAudio, setIsRecordingAudio } = useMessageState();
  const { chat, showChat } = useChatState();
  const [text, setText] = useState('');
  const plugins = [emojiPlugin];

  const keyPressed = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (text && event.key === "Enter" && !event.shiftKey && !event.ctrlKey) {
      event.preventDefault();
      setEditorState(EditorState.createEmpty());
      sendMessage({
        type: MessageType.Text,
        body: {
          body: text
        }
      }).then(() => {
        setText('');
      });
      return 'handled';
    }

    return 'not-handled';
  }, [setText, setEditorState, text, sendMessage]);

  const onChange = useCallback((newEditorState: EditorState) => {
    const currentContentTextLength = editorState.getCurrentContent().getPlainText().length;
    const newContentTextLength = newEditorState.getCurrentContent().getPlainText().length;
  
    if (currentContentTextLength === 0 && newContentTextLength === 1) {
      setEditorState(EditorState.moveFocusToEnd(newEditorState));
    } else {
      setEditorState(newEditorState);
    }
  },[setEditorState, editorState]);

  useEffect(() => {
    const blocks = convertToRaw(editorState.getCurrentContent()).blocks;
    const value = blocks.map(block => block.text.trim()).join('\n');
    setText(value);
  }, [editorState]);

  const handleSendClick = useCallback(() => {
    setEditorState(EditorState.createEmpty());
    sendMessage({
      type: MessageType.Text,
      body:{
        body: text
      } 
    }).then(() => {
      setText('');
    })
  }, [sendMessage, text])

  const recordAudio = useCallback(() => {
    navigator.getUserMedia({ audio: true },
      () => {
        setIsRecordingAudio(true);
        if (!isReady) setIsReady(true);
      },
      () => {
        setIsReady(false);
      }
    );
  }, [setIsRecordingAudio, isReady]);

  const getButton = useMemo(() => 
    (hasText: boolean) => {
      if (hasText || !chat) {
        return (
          <SendButton disableRipple={true} disableTouchRipple={true} onClick={handleSendClick}>
            <SendIcon />
          </SendButton>
        );
      } else {
        return (
          <SendButton disableRipple={true} disableTouchRipple={true} onClick={recordAudio}>
            <MicIcon />
          </SendButton>
        );
      }
    }
  , [handleSendClick, recordAudio, chat]);

  return (
    <Container as="sdiv" styles={{...textInput}}>
      <EditorWrapper as="sdiv">
        <StyledEditor 
          editorState={editorState}
          readOnly={isRecordingAudio}
          spellCheck={true}
          onChange={onChange}
          plugins={plugins}
          placeholder={showChat ? getTranslation('TEXT_INPUT_PLACEHOLDER') : ''}
          handleReturn={keyPressed}
        />
      </EditorWrapper>
      <SendContainer as="sdiv" active={isRecordingAudio} animate={isReady}>
      {!isRecordingAudio ? 
        getButton(!!text) 
      :
        <Audio />
      }
      </SendContainer>
    </Container>
  );
}

export default TextInput;